import {computed, inject, Injectable, Signal, signal} from "@angular/core";
import {NavigationEnd, Router, Event, ActivatedRoute} from "@angular/router";
import {filter, map, Observable, switchMap, tap} from "rxjs";

interface NavigationState {
  currentUrl: string;
  activeComponent: string;
  navigationHistory: string[]
}

@Injectable({
  providedIn: "root",
})
export class NavigationService {
  router = inject(Router);
  route = inject(ActivatedRoute);
  private routerEvents$: Observable<Event> = this.router.events;
  skipDeactivateGuard = signal<boolean>(false);

  private state = signal<NavigationState>({
    currentUrl: "",
    activeComponent: "",
    navigationHistory: []
  });

  currentUrl = computed(() => this.state().currentUrl);
  activeComponent = computed(() => this.state().activeComponent);
  navigationHistory = computed(() => this.state().navigationHistory);

  constructor() {
    this.routerEvents$
      .pipe(
        filter(
          (event: Event): event is NavigationEnd =>
            event instanceof NavigationEnd
        ),
        tap((event) => console.log(event)),
        switchMap(() => this.getActiveComponent(this.route)),
      )
      .subscribe(([url, componentName]: [string, string]) => {
        this.state.update((state) => {
          const history = [...state.navigationHistory];
          if (history.length === 0 || history[history.length - 1] !== url) {
            history.push(url);
          }
          return {
            ...state,
            currentUrl: url,
            activeComponent: componentName,
            navigationHistory: history
          };
        });
      })
  }

  public allowNavigation() {
    this.skipDeactivateGuard.set(true)
  }

  public preventNavigation() {
    this.skipDeactivateGuard.set(false)
  }

  private getActiveComponent(
    route: ActivatedRoute
  ): Observable<[string, string]> {
    return route.firstChild
      ? this.getActiveComponent(route.firstChild)
      : route.data.pipe(
        map((data) => [this.router.url, data["componentName"] || ""])
      );
  }

  public getPreviousUrl(): string {
    const history = this.navigationHistory();
    if (history.length < 2) {
      return '/dashboard'
    }
    const previousUrl = history[history.length - 2];
    this.state.update(state => ({
      ...state,
      navigationHistory: state.navigationHistory.slice(0, -1)
    }))

    return previousUrl;
  }

  public navigateBack() {
    this.router.navigateByUrl(this.getPreviousUrl());
  }

}
